import React, { useState, useCallback, useEffect } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  MenuItem,
  Paper,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Chip,
  Avatar,
  CircularProgress,
} from "@mui/material";
import {
  AccountCircle,
  Phone,
  Lock,
  Public,
  Language,
  Info,
  Star,
  EmojiEvents,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useDropzone } from "react-dropzone";
import { headlineOptions } from "../Constant/InstructorHeadlineOptions";
import { expertiseOptions } from "../Constant/ExpertiseOptions";
import { educationalBackgroundOptions } from "../Constant/EducationalBackgroundOptions";
import { useSelector, useDispatch } from "react-redux";
import {
  getCurrentInstructorDetail,
  resetAllActions,
  updateInstructorDetail,
} from "../../State/Instructor/Action";
import { updateLearner } from "../../State/Learner/Action";
import { useNavigate } from "react-router-dom";

const InstructorRegistration = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    username: "",
    password: "",
    title: "",
    headline: "",
    bio: "",
    linkedIn: "",
    website: "",
    experience: "",
    expertise: [],
    education: "",
    certifications: "",
    languages: [],
  });

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();

  const dispatch = useDispatch();

  const instructor = useSelector(
    (state) => state.instructor?.currentInstructor?.data
  );

  const { detailUpdateResult, errorDetailUpdate, loadingDetailUpdate } =
    useSelector((store) => store.instructor);

  useEffect(() => {
    dispatch(resetAllActions());
    dispatch(getCurrentInstructorDetail());
  }, [dispatch]);

  useEffect(() => {
    if (instructor) {
      setFormData({
        fullName: instructor.fullName || "",
        phone: instructor.phone || "",
        username: instructor.username || "",
        password: "",
        title: instructor.title || "",
        headline: instructor.headline || "",
        bio: instructor.bio || "",
        linkedIn: instructor.linkedIn || "",
        website: instructor.website || "",
        experience: instructor.experience || "",
        expertise: instructor.expertise || [],
        education: instructor.education || "",
        certifications: instructor.certifications || "",
        languages: instructor.languages || [],
      });

      if (instructor.profilePictureUrl) {
        setPreviewUrl(instructor.profilePictureUrl);
      }
    }
  }, [instructor]);

  useEffect(() => {
    if (detailUpdateResult) {
      navigate("/profile-detail");
    }
    if (errorDetailUpdate) {
      console.error("Error updating instructor details:", errorDetailUpdate);
    }
  }, [detailUpdateResult, errorDetailUpdate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles && acceptedFiles.length > 0) {
      const uploadedFile = acceptedFiles[0];
      setFile(uploadedFile);
      setPreviewUrl(URL.createObjectURL(uploadedFile));
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { "image/*": [] },
    multiple: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    const formPayload = new FormData();
    if (file) formPayload.append("file", file);
    formPayload.append(
      "instructorDto",
      new Blob([JSON.stringify(formData)], { type: "application/json" })
    );

    dispatch(updateInstructorDetail(formPayload));
  };

  return (
    <Box
      sx={{
        background: "linear-gradient(to right, #f3e5f5, #e1f5fe)",
        minHeight: "100vh",
        py: 6,
        px: 2,
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 60 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper
          elevation={6}
          sx={{
            p: 4,
            maxWidth: 950,
            mx: "auto",
            borderRadius: 4,
            backgroundColor: "#fffefecc",
          }}
        >
          <Typography variant="h4" gutterBottom align="center">
            👨‍🏫 Instructor Registration
          </Typography>

          <form onSubmit={handleSubmit}>
            <Typography variant="h6" gutterBottom>
              👤 Personal Information
            </Typography>

            <Box
              {...getRootProps()}
              sx={{
                border: "2px dashed #ccc",
                borderRadius: 2,
                p: 2,
                mb: 3,
                textAlign: "center",
                backgroundColor: isDragActive ? "#f0f0f0" : "#fafafa",
                cursor: "pointer",
              }}
            >
              <input {...getInputProps()} />
              {uploading ? (
                <CircularProgress size={24} />
              ) : previewUrl ? (
                <Avatar
                  src={previewUrl}
                  alt="Profile"
                  sx={{ width: 100, height: 100, mx: "auto", mb: 1 }}
                />
              ) : (
                <Typography>
                  📤 Drag & drop a profile picture here, or click to select
                </Typography>
              )}
            </Box>

            <Grid container spacing={2}>
              {[
                {
                  name: "fullName",
                  label: "Full Name",
                  icon: <AccountCircle />,
                },

                { name: "phone", label: "Phone Number", icon: <Phone /> },
                {
                  name: "username",
                  label: "Username",
                  icon: <AccountCircle />,
                },
                {
                  name: "password",
                  label: "Password",
                  icon: <Lock />,
                  type: "password",
                },
              ].map(({ name, label, icon, type }) => (
                <Grid key={name} item xs={12} sm={6}>
                  <TextField
                    label={label}
                    name={name}
                    type={type || "text"}
                    fullWidth
                    required={name !== "password"}
                    value={formData[name]}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{icon}</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              ))}

              <Grid item xs={12} sm={4}>
                <TextField
                  label="Title"
                  name="title"
                  select
                  fullWidth
                  value={formData.title}
                  onChange={handleChange}
                >
                  {["Mr.", "Ms.", "Dr.", "Prof."].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={8}>
                <FormControl fullWidth>
                  <InputLabel>Professional Headline</InputLabel>
                  <Select
                    name="headline"
                    value={formData.headline}
                    onChange={handleChange}
                    label="Professional Headline"
                  >
                    {headlineOptions.map(({ category, options }) => [
                      <MenuItem
                        key={`cat-${category}`}
                        disabled
                        style={{
                          backgroundColor: "#616161",
                          color: "#fff",
                          fontWeight: "bold",
                          textAlign: "center",
                        }}
                      >
                        {category}
                      </MenuItem>,
                      ...options.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      )),
                    ])}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Biography / About Me"
                  name="bio"
                  multiline
                  rows={4}
                  fullWidth
                  value={formData.bio}
                  onChange={handleChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Info />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              {[
                {
                  name: "linkedIn",
                  label: "LinkedIn Profile",
                  icon: <Public />,
                },
                {
                  name: "website",
                  label: "Website / Portfolio",
                  icon: <Language />,
                },
                {
                  name: "experience",
                  label: "Years of Experience",
                  icon: <Star />,
                  type: "number",
                },
                {
                  name: "certifications",
                  label: "Certifications / Awards",
                  icon: <EmojiEvents />,
                },
              ].map(({ name, label, icon, type }) => (
                <Grid key={name} item xs={12} sm={6}>
                  <TextField
                    label={label}
                    name={name}
                    type={type || "text"}
                    fullWidth
                    value={formData[name]}
                    onChange={handleChange}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">{icon}</InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              ))}

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Areas of Expertise</InputLabel>
                  <Select
                    name="expertise"
                    multiple
                    value={formData.expertise}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        expertise: e.target.value,
                      }))
                    }
                    renderValue={(selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} />
                        ))}
                      </Box>
                    )}
                  >
                    {expertiseOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Educational Background</InputLabel>
                  <Select
                    name="education"
                    value={formData.education}
                    onChange={handleChange}
                    label="Educational Background"
                  >
                    {educationalBackgroundOptions.map(
                      ({ category, options }) => [
                        <MenuItem
                          key={`cat-${category}`}
                          disabled
                          style={{
                            backgroundColor: "#616161",
                            color: "#fff",
                            fontWeight: "bold",
                            textAlign: "center",
                          }}
                        >
                          {category}
                        </MenuItem>,
                        ...options.map((opt) => (
                          <MenuItem key={opt} value={opt}>
                            {opt}
                          </MenuItem>
                        )),
                      ]
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Languages Spoken</InputLabel>
                  <Select
                    name="languages"
                    multiple
                    value={formData.languages}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        languages: e.target.value,
                      }))
                    }
                    renderValue={(selected) => selected.join(", ")}
                  >
                    {[
                      "English",
                      "Hindi",
                      "Spanish",
                      "French",
                      "German",
                      "Chinese",
                      "Arabic",
                      "Portuguese",
                    ].map((language) => (
                      <MenuItem key={language} value={language}>
                        {language}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box mt={4} textAlign="center">
              <motion.div whileHover={{ scale: 1.05 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  disabled={uploading}
                >
                  🚀 Register Instructor
                </Button>
              </motion.div>
            </Box>
          </form>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default InstructorRegistration;
